import React from 'react';
import { useResponseMessages } from '../../hooks/useResponseMessage';
import './EngagementMessage.css';

/**
 * Enhanced message component specifically for engagement messages
 * Supports celebrations, warnings, milestones, and interactive actions
 */
const EngagementMessage = ({ 
    messageData, 
    onAction, 
    onDismiss 
}) => {
    const { type, priority, title, message, actions = [], confetti = false } = messageData;

    const getMessageIcon = () => {
        switch (type) {
            case 'celebration':
            case 'ultimate_celebration':
                return '🎉';
            case 'milestone':
                return '🎯';
            case 'streak':
                return '🔥';
            case 'warning':
                return '⚠️';
            case 'encouragement':
                return '💪';
            default:
                return '📢';
        }
    };

    const getMessageClass = () => {
        const baseClass = 'engagement-message';
        const typeClass = `engagement-message--${type}`;
        const priorityClass = priority === 'ultimate' ? 'engagement-message--ultimate' : '';
        
        return [baseClass, typeClass, priorityClass].filter(Boolean).join(' ');
    };

    const handleActionClick = (action) => {
        if (onAction) {
            onAction(action, messageData);
        }
        
        // Handle built-in actions
        switch (action.action) {
            case 'navigate':
                window.location.href = action.target;
                break;
            case 'share':
                // Implement sharing logic
                handleShare(action.data);
                break;
            case 'encourage':
                // Just dismiss with positive reinforcement
                onDismiss?.();
                break;
            default:
                break;
        }
    };

    const handleShare = (data) => {
        if (navigator.share) {
            navigator.share({
                title: 'Financial Milestone Achieved!',
                text: message,
                url: window.location.href
            });
        } else {
            // Fallback: copy to clipboard
            navigator.clipboard.writeText(`${title}: ${message}`);
            alert('Achievement copied to clipboard!');
        }
    };

    // Trigger confetti effect for ultimate celebrations
    React.useEffect(() => {
        if (confetti && window.confetti) {
            window.confetti({
                particleCount: 100,
                spread: 70,
                origin: { y: 0.6 }
            });
        }
    }, [confetti]);

    return (
        <div className={getMessageClass()}>
            <div className="engagement-message__header">
                <span className="engagement-message__icon">
                    {getMessageIcon()}
                </span>
                <h3 className="engagement-message__title">
                    {title}
                </h3>
                {onDismiss && (
                    <button 
                        className="engagement-message__dismiss"
                        onClick={onDismiss}
                        aria-label="Dismiss message"
                    >
                        ×
                    </button>
                )}
            </div>

            <div className="engagement-message__content">
                <p dangerouslySetInnerHTML={{ __html: message }} />
                
                {messageData.data && messageData.data.interestSaved && (
                    <div className="engagement-message__stats">
                        <small>Interest saved: {messageData.data.interestSaved}</small>
                    </div>
                )}
            </div>

            {actions.length > 0 && (
                <div className="engagement-message__actions">
                    {actions.map((action, index) => (
                        <button
                            key={index}
                            className={`engagement-message__action ${action.primary ? 'engagement-message__action--primary' : ''}`}
                            onClick={() => handleActionClick(action)}
                        >
                            {action.label}
                        </button>
                    ))}
                </div>
            )}

            {type === 'ultimate_celebration' && (
                <div className="engagement-message__celebration-extras">
                    <div className="engagement-message__achievement-badge">
                        🏆 DEBT FREE 🏆
                    </div>
                </div>
            )}
        </div>
    );
};

export default EngagementMessage;